/**
 * The commercial insurance glossary.
 *
 * Josh, 2026-09-05: "It might be good to have a glossary, as comprehensive as
 * possible of as many commercial insurance terms and phrases as possible. This
 * portal is to be used by brand new to commercial agents and seasoned vets."
 *
 * ⚠️ SEARCH HAS TO MATCH THE ABBREVIATION, NOT THE FORMAL NAME. Nobody types
 * "certificate of insurance" — they type COI, or cert, or "acord 25". Every
 * entry carries an `aka` list and the matcher weights a hit there as highly as
 * a hit on the term itself. An entry findable only by its proper name is an
 * entry nobody finds, which is the failure mode of most glossaries.
 *
 * ⚠️ AND IT NEVER RETURNS NOTHING WHEN A REAL WORD WAS TYPED. A definition body
 * is searched too, at lower weight, so "who pays first" finds primary and
 * noncontributory even though those words are not in its title.
 */
import { FOUNDATION_TERMS } from './terms.foundations.js';
import { LINE_TERMS } from './terms.lines.js';
import { LIABILITY_TERMS } from './terms.liability.js';
import { MARKET_TERMS } from './terms.market.js';
import { CLAIMS_TERMS } from './terms.claims.js';
import { SPECIALTY_TERMS } from './terms.specialty.js';
export * from './types.js';
/**
 * Every term, sorted alphabetically.
 *
 * ⚠️ SORTED ONCE HERE RATHER THAN IN THE PAGE. Several surfaces will read this
 * — the glossary page, an inline definition popover, Connor — and an ordering
 * decided per surface is an ordering that eventually disagrees with itself.
 */
export const GLOSSARY = [
    ...FOUNDATION_TERMS,
    ...LINE_TERMS,
    ...LIABILITY_TERMS,
    ...MARKET_TERMS,
    ...CLAIMS_TERMS,
    ...SPECIALTY_TERMS,
].sort((a, b) => a.term.localeCompare(b.term, 'en', { sensitivity: 'base' }));
const BY_ID = new Map(GLOSSARY.map((t) => [t.id, t]));
export const glossaryTerm = (id) => BY_ID.get(id);
/** The short list for somebody new to commercial lines. */
export const GLOSSARY_ESSENTIALS = GLOSSARY.filter((t) => t.essential);
/**
 * The `see` links of a term, resolved and with anything dangling dropped.
 *
 * ⚠️ DROPPED RATHER THAN RENDERED AS A DEAD LINK. A typo in a `see` array
 * should not put a link on screen that goes nowhere. `glossaryProblems()`
 * below is what actually catches the typo, and it runs in the build.
 */
export function glossarySeeAlso(term) {
    return (term.see ?? []).map((id) => BY_ID.get(id)).filter((t) => Boolean(t));
}
/**
 * Everything wrong with the glossary data, as human-readable lines.
 *
 * ⚠️ A FUNCTION RATHER THAN A THROW AT IMPORT TIME. Throwing here would take
 * the whole hub down over a broken cross-reference in a glossary — a wildly
 * disproportionate failure. `npm run check:glossary` fails the build instead,
 * so the mistake is caught before it ships and is harmless if one ever slips.
 */
export function glossaryProblems() {
    const problems = [];
    const seen = new Set();
    for (const t of GLOSSARY) {
        if (seen.has(t.id))
            problems.push(`duplicate id: ${t.id}`);
        seen.add(t.id);
        if (!t.def.trim())
            problems.push(`${t.id}: empty definition`);
        for (const ref of t.see ?? []) {
            if (!BY_ID.has(ref))
                problems.push(`${t.id}: see → unknown term "${ref}"`);
            if (ref === t.id)
                problems.push(`${t.id}: see → itself`);
        }
        for (const alias of t.aka ?? []) {
            if (!alias.trim())
                problems.push(`${t.id}: empty alias`);
        }
    }
    return problems;
}
/** Everything a term can be found by, lowercased. */
const haystack = (t) => ({
    name: t.term.toLowerCase(),
    alias: [t.short ?? '', ...(t.aka ?? [])].join(' | ').toLowerCase(),
    body: `${t.def} ${t.detail ?? ''} ${t.confused ?? ''}`.toLowerCase(),
});
const HAYSTACKS = new Map(GLOSSARY.map((t) => [t.id, haystack(t)]));
/**
 * Score one term against a query. 0 means no match.
 *
 * The weights encode what a person means when they type. An exact
 * abbreviation — "COI", "EMR", "SIR" — is almost always someone who knows
 * exactly what they want, so it outranks a term whose definition happens to
 * mention the letters.
 */
function score(t, q) {
    const h = HAYSTACKS.get(t.id);
    if (!h)
        return 0;
    const short = (t.short ?? '').toLowerCase();
    if (short && short === q)
        return 100;
    if (h.name === q)
        return 95;
    if ((t.aka ?? []).some((a) => a.toLowerCase() === q))
        return 90;
    if (h.name.startsWith(q))
        return 70;
    if (h.alias.split(' | ').some((a) => a.startsWith(q)))
        return 60;
    if (h.name.includes(q))
        return 45;
    /*
      ⚠️ THE QUERY CONTAINING THE TERM, NOT ONLY THE OTHER WAY ROUND. Someone
      searching "business income coverage" means the entry called "Business
      income" — but the name does not start with, equal, or contain that query, so
      every check above misses and the entry scored zero. It lost to Civil
      authority, whose *definition* happens to contain the phrase "business income
      coverage". A body match beating the actual term is the worst kind of search
      failure: it is confidently wrong rather than empty.
  
      ⚠️ BUT THE NAME MUST BE MOST OF THE QUERY, AND THAT GUARD IS NOT OPTIONAL.
      Without it "the appetite for roofers" contains "appetite" and this rule
      answers a live market question with a dictionary entry — a far worse failure
      than the one it was added to fix. "business income coverage" is 65% the term
      name; "appetite for roofers" is 40%. Sixty percent separates a term someone
      padded from a sentence that merely mentions one.
  
      Five characters is also a floor, because "Loss" and "Risk" are terms and
      almost any insurance question contains one of them.
    */
    if (h.name.length >= 5 && q.includes(h.name) && h.name.length / q.length >= 0.6)
        return 40;
    if (h.alias.includes(q))
        return 35;
    if (h.body.includes(q))
        return 15;
    return 0;
}
/**
 * Search and filter, in one place.
 *
 * ⚠️ RANKED WHEN SEARCHING, ALPHABETICAL WHEN BROWSING. Those are two different
 * activities: a search for "SIR" wants the best answer first, while browsing
 * the S's wants the order the eye expects. Ranking a browse would make the list
 * shuffle every time a filter changed.
 */
export function searchGlossary(query) {
    const q = (query.text ?? '').trim().toLowerCase();
    const pool = GLOSSARY.filter((t) => (!query.category || t.category === query.category) &&
        (!query.essentialsOnly || t.essential) &&
        (!query.letter || t.term.toUpperCase().startsWith(query.letter.toUpperCase())));
    if (!q)
        return pool;
    return pool
        .map((t) => ({ t, s: score(t, q) }))
        .filter((r) => r.s > 0)
        .sort((a, b) => b.s - a.s || a.t.term.localeCompare(b.t.term))
        .map((r) => r.t);
}
/** The initial letters that actually have terms behind them. */
export const GLOSSARY_LETTERS = [...new Set(GLOSSARY.map((t) => t.term[0].toUpperCase()))]
    .filter((c) => /[A-Z]/.test(c))
    .sort();
export const GLOSSARY_COUNT = GLOSSARY.length;
